import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../../../config";
import { getAuthHeaders } from "../../../services/useAuth";
import CustomButton from "../../../pages/CustomButton";
import Loader from "../../../dist/Loader";
import * as XLSX from "xlsx";

const ProductListSlab = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [products, setProducts] = useState([]);

  // Filters
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedYearRange, setSelectedYearRange] = useState("");
  const [selectedMonthRange, setSelectedMonthRange] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const tableRef = useRef(null);

  const yearRanges = ["2025-2026"];

  const halfYearOptions = [
    { value: "Apr-Sep", label: "Apr - Sep" },
    { value: "Oct-Mar", label: "Oct - Mar" },
  ];

  const computeCurrentFiscalYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    if (month >= 4) {
      return `${year}-${year + 1}`;
    } else {
      return `${year - 1}-${year}`;
    }
  };

  const computeCurrentHalfYear = () => {
    const month = new Date().getMonth() + 1;
    if (month >= 4 && month <= 9) {
      return "Apr-Sep";
    }
    return "Oct-Mar";
  };

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchStates(), fetchDivisions()]);
      setSelectedYearRange((prev) => prev || computeCurrentFiscalYear());
      setSelectedMonthRange((prev) => prev || computeCurrentHalfYear());
    };
    init();
  }, []);

  const fetchStates = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/states`, {
        headers: getAuthHeaders(),
      });

      const raw = res.data || [];
      const list = Array.isArray(raw) ? raw : raw.data || [];

      setStates(list);
      if (list.length > 0) {
        setSelectedState((prev) => prev || String(list[0].id));
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load states");
    }
  };

  const fetchDivisions = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/divisions`, {
        headers: getAuthHeaders(),
      });
      const list = res.data || [];
      setDivisions(list);
      if (list.length > 0) {
        setSelectedDivision((prev) => prev || String(list[0].id));
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load divisions");
    }
  };

  const handleGoClick = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (selectedState) {
        params.set("state_id", selectedState);
      }
      if (selectedDivision) {
        params.set("division_id", selectedDivision);
      }
      if (selectedYearRange) {
        params.set("year", selectedYearRange);
      }
      if (selectedMonthRange) {
        params.set("month", selectedMonthRange);
      }

      const url = `${BASE_URL}/api/product/?${params.toString()}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });

      setProducts(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load products");
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    if (!tableRef.current || products.length === 0) return;

    const divisionName =
      divisions.find((d) => String(d.id) === String(selectedDivision))?.name ||
      "";
    const stateName =
      states.find((s) => String(s.id) === String(selectedState))?.name || "";

    const headerHtml = `
      <div style="font-family: Arial, sans-serif; margin-bottom: 10px;">
        <h3 style="margin:0 0 4px 0;">Current Active Product List</h3>
        <div style="font-size:12px; color:#444;">
          <strong>Division:</strong> ${divisionName || "-"} &nbsp; | &nbsp;
          <strong>State:</strong> ${stateName || "-"} &nbsp; | &nbsp;
          <strong>Year:</strong> ${selectedYearRange || "-"} &nbsp; | &nbsp;
          <strong>Period:</strong> ${selectedMonthRange || "-"}
        </div>
      </div>
    `;

    const tableHTML = tableRef.current.outerHTML;

    const win = window.open("", "_blank", "width=1200,height=800");
    if (!win) {
      alert("Please allow pop-ups to print this report.");
      return;
    }

    win.document.write(`
      <html>
        <head>
          <title>Current Active Product List</title>
          <style>
            * { box-sizing: border-box; font-family: Arial, sans-serif; }
            table { border-collapse: collapse; width: 100%; font-size: 11px; }
            th, td { border: 1px solid #ccc; padding: 4px 6px; }
            thead th { background: #cfeefb; font-weight: 600; }
          </style>
        </head>
        <body>
          ${headerHtml}
          ${tableHTML}
          <script>
            window.onload = function() {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  const handleExportExcel = () => {
    if (!tableRef.current || products.length === 0) return;

    const ws = XLSX.utils.table_to_sheet(tableRef.current);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ProductList");

    const divisionName =
      divisions.find((d) => String(d.id) === String(selectedDivision))?.name ||
      "All";
    const fileName = `product-list-${divisionName}-${selectedYearRange}-${selectedMonthRange}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {/* FILTERS */}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-center text-xl sm:text-2xl font-bold text-gray-800 mb-4">
          <span className="text-blue-700">Current Active </span>
          <span className="text-blue-500">Product List </span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Division */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-gray-700 w-24">
              Division
            </label>
            <select
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              className="flex-1 h-10 rounded-md border border-gray-300 px-3 text-sm"
            >
              <option value="" disabled>
                Select Division
              </option>
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-gray-700 w-24">
              Year
            </label>
            <select
              value={selectedYearRange}
              onChange={(e) => setSelectedYearRange(e.target.value)}
              className="flex-1 h-10 rounded-md border border-gray-300 px-3 text-sm"
            >
              <option value="" disabled>
                Select Year
              </option>
              {yearRanges.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
          </div>

          {/* Months */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-gray-700 w-24">
              Months
            </label>
            <select
              value={selectedMonthRange}
              onChange={(e) => setSelectedMonthRange(e.target.value)}
              className="flex-1 h-10 rounded-md border border-gray-300 px-3 text-sm"
            >
              <option value="" disabled>
                Select Period
              </option>
              {halfYearOptions.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* GO BUTTON */}
        <div className="flex justify-center mt-4">
          <CustomButton
            onClick={handleGoClick}
            variant="save"
            disabled={isLoading}
          >
            {isLoading ? <Loader /> : "Go"}
          </CustomButton>
        </div>

        {error && (
          <div className="text-sm text-red-600 mt-2 text-center">{error}</div>
        )}
      </div>

      {/* PRODUCTS TABLE */}
      {products.length > 0 && (
        <div
          className="border rounded shadow no-scrollbar mt-5"
          style={{ maxHeight: "101vh", overflowY: "auto", overflowX: "auto" }}
        >
          <table
            ref={tableRef}
            className="min-w-full text-sm unlock-dcr-leave-table"
          >
            <thead className="bg-blue-500 text-white">
              <tr>
                <th>S.No</th>
                <th>Division</th>
                <th>Product Code</th>
                <th>Product Name</th>
                <th>Packaging</th>
                <th>R Rate</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, idx) => {
                const price =
                  p.prices && p.prices.length > 0 ? p.prices[0] : null;
                return (
                  <tr key={p.id || idx}>
                    <td>{idx + 1}</td>
                    <td>
                      {(p.division_ids || [])
                        .map((d) => d.name)
                        .join(", ") || "-"}
                    </td>
                    <td>{p.id}</td>
                    <td>{p.name || p.product_name}</td>
                    <td>{p.packaging || "-"}</td>
                    <td>{price ? price.r_rate : "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton
              onClick={handleExportExcel}
              label="Export"
              variant="export"
            >
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductListSlab;
